import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import React from 'react';

import { Button } from '@commons/utils/styledLibraryComponents';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { PeriodDatePicker } from '@commons/DatePickers/PeriodDatePicker';
import { usePeriodDatePickerState } from '@hooks/usePeriodDatePickerState';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { syncPayroll } from '@services/integrations';

import {
  PayrollSyncButtonHost,
  PayrollSyncDescription,
  PayrollSyncPanelRoot,
} from './styledComponents';

const PAYROLL_SYNC_MAX_PAST_YEARS = 2;
const PAYROLL_SYNC_TIMEZONE = 'Europe/Paris';

export const PayrollSyncPanel = ({
  onSyncSuccess,
  partnerName,
  showErrorMessage,
  showSuccessMessage,
  storeIds,
}) => {
  const { t } = useTranslation();
  const { startDate, endDate, handleSelectedDates, focusedDateInput, setFocusedDateInput } =
    usePeriodDatePickerState(null, null, PAYROLL_SYNC_TIMEZONE);
  const [isSyncing, setIsSyncing] = useState(false);

  const maxPastDate = useMemo(
    () => moment.tz(PAYROLL_SYNC_TIMEZONE).subtract(PAYROLL_SYNC_MAX_PAST_YEARS, 'years'),
    [],
  );
  const maxFutureDate = useMemo(() => moment.tz(PAYROLL_SYNC_TIMEZONE), []);

  const hasSelectedRange = Boolean(startDate && endDate);
  const canSync = hasSelectedRange && storeIds.length > 0 && !isSyncing;

  const handleSync = useCallback(async () => {
    if (!canSync) {
      return;
    }

    setIsSyncing(true);

    try {
      await syncPayroll({
        startDate: startDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        endDate: endDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        storeIds,
      });

      showSuccessMessage(t('ADMIN.INTEGRATIONS.PAYROLL_SYNC_SUCCESS'));
      onSyncSuccess();
    } catch {
      setIsSyncing(false);
      showErrorMessage(t('ADMIN.INTEGRATIONS.PAYROLL_SYNC_ERROR'));
    }
  }, [
    canSync,
    endDate,
    onSyncSuccess,
    showErrorMessage,
    showSuccessMessage,
    startDate,
    storeIds,
    t,
  ]);

  return (
    <PayrollSyncPanelRoot>
      <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.H2}>
        {t('ADMIN.INTEGRATIONS.PAYROLL_SYNC_TITLE')}
      </Text>
      <PayrollSyncDescription>
        <Text color={ENUM_COLORS.IP_BLACK_1} font={ENUM_FONTS.TEXT_INTER_REGULAR_12_HEIGHT_20}>
          {t('ADMIN.INTEGRATIONS.PAYROLL_SYNC_DESCRIPTION', { partner: partnerName })}
        </Text>
      </PayrollSyncDescription>
      <PeriodDatePicker
        disabled={isSyncing}
        endDate={endDate}
        focusedDateInput={focusedDateInput}
        maxFutureDate={maxFutureDate}
        maxPastDate={maxPastDate}
        numberOfMonths={1}
        setFocusedDateInput={setFocusedDateInput}
        startDate={startDate}
        timezone={PAYROLL_SYNC_TIMEZONE}
        onDatesChange={handleSelectedDates}
      />
      <PayrollSyncButtonHost>
        <Button
          color="inpulse-default"
          handleClick={handleSync}
          icon="/images/inpulse/sync-white-small.svg"
          isDisabled={!canSync}
          label={t('ADMIN.INTEGRATIONS.PAYROLL_SYNC_BUTTON')}
          iconOnLeft
        />
      </PayrollSyncButtonHost>
    </PayrollSyncPanelRoot>
  );
};

export default PayrollSyncPanel;
